// Link editor transitions (Lexical `LinkMentionPlugin`: openLinkEditor / applyLink / closeLinkEditor, Mod+K).
// Hosts own the dialog, focus and how intents are dispatched; every document decision is made here.
import type { EditorIntent } from './intents'
import { safeLinkHref, selectedLink } from './rich-content'
import type { CoreState } from './state'

/** The open link editor: the URL being typed, whether opening it linked the range to a preview `https://` link, and
 * the links it edits (`selectedLinkIds` once open), which Apply updates by identity. */
export interface LinkEdit { url: string; preview: boolean; links?: string[] }

function collapsed(state: CoreState) {
  const selection = state.selection
  return !!selection && selection.anchor.blockId === selection.focus.blockId && selection.anchor.offset === selection.focus.offset
}

/** Opens on a range or caret; `preview` links a plain range to `https://` first (`intent`). Null when a link cannot be edited here. */
export function openLink(state: CoreState, preview: boolean): { edit: LinkEdit; intent?: EditorIntent } | null {
  const selection = state.selection
  if (!selection || selection.nodes?.length || selection.table) return null
  const existing = selectedLink(state)
  const previewing = preview && existing === null && !collapsed(state)
  return previewing
    ? { edit: { url: 'https://', preview: true }, intent: { type: 'setLink', url: 'https://' } }
    : { edit: { url: existing ?? '', preview: false } }
}

/** Applying a trimmed URL: a bare caret inserts the URL as its own link text; otherwise the selection is linked. Empty does nothing. */
export function applyLink(state: CoreState, edit: LinkEdit): EditorIntent | null {
  const raw = edit.url.trim()
  if (!raw) return null
  const url = safeLinkHref(raw)
  if (url === null) return null
  if (edit.links?.length) return { type: 'setLink', url, links: edit.links }
  if (collapsed(state) && selectedLink(state) === null) {
    return { type: 'insertFromPaste', md: '', fragment: [{ id: '', type: 'p', children: [{ id: '', type: 'a', href: url, children: [{ id: '', type: 'text', text: url }] }] }] }
  }
  return { type: 'setLink', url }
}

/** Closing without applying removes the preview link. */
export function closeLink(edit: LinkEdit): EditorIntent | null {
  return edit.preview ? { type: 'removeLink' } : null
}
